/**
 * designSignature — a stable fingerprint of the *simulation-affecting* inputs of a
 * building. Used to detect when an uploaded results report has gone stale because the
 * design changed after the report was generated.
 *
 * Excludes fields that do NOT change the EnergyPlus simulation output (purely
 * descriptive / compliance-categorisation fields, which the Results screen already
 * re-evaluates live), and the raw EPW content (huge and re-derivable). Any change to
 * geometry, constructions, fenestration, loads, schedules, HVAC, weather metadata,
 * orientation, etc. changes the signature.
 */

// Fields that don't affect simulation results (only labels / live-recomputed compliance).
// `ecbc_building_subtype` only selects the ECBC 2017 §9.5 EPI-Ratio limit row; it never reaches
// an IDF, so changing it must not mark a generated Standard Design (or a loaded report) stale.
// `cfd` holds the CFD Analysis wizard's own inputs and run records (src/engine/cfd/). They never
// reach the EnergyPlus IDF, so editing them must not mark a baseline or a loaded report stale.
const NON_SIM_KEYS = new Set(['name', 'building_category', 'compliance_code', 'study_type',
  'ecbc_building_subtype', 'cfd']);

/** Deterministic, key-sorted JSON so object key order never affects the signature. */
function stableStringify(value) {
  if (value === null || typeof value !== 'object') {
    const s = JSON.stringify(value);
    return s === undefined ? 'null' : s;
  }
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  // Skip internal/transient keys (e.g. floor._index added in place by generateIDF) so
  // exporting an IDF doesn't perturb the fingerprint.
  const keys = Object.keys(value).filter((k) => !k.startsWith('_')).sort();
  return `{${keys.map((k) => JSON.stringify(k) + ':' + stableStringify(value[k])).join(',')}}`;
}

/** Compact djb2 → base36 hash (change-detection fingerprint, not cryptographic). */
function hashString(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = (((h << 5) + h) ^ s.charCodeAt(i)) >>> 0;
  return h.toString(36);
}

export function designSignature(building) {
  if (!building || typeof building !== 'object') return '';
  const subset = {};
  for (const k of Object.keys(building)) {
    if (NON_SIM_KEYS.has(k)) continue;
    if (k === 'weather_file' && building[k] && typeof building[k] === 'object') {
      const { content, ...wfRest } = building[k]; // drop raw EPW text from the fingerprint
      subset[k] = wfRest;
    } else {
      subset[k] = building[k];
    }
  }
  return hashString(stableStringify(subset));
}
